<template>
  <div class="import-job-list-card box p-0" :class="cardClass">
    <div class="import-job-list-card__content box-content">
      <div class="import-job-list-card__row columns is-vcentered is-mobile is-variable is-1 is-marginless">
        <div class="column p-0">
          <div class="import-job-list-card__title has-text-weight-bold">Job: {{ job.id }}</div>
          <div v-if="importerLabel" class="import-job-list-card__meta has-text-grey-light">
            Importer: {{ importerLabel }}
          </div>
          <div class="import-job-list-card__meta has-text-grey-light">Created: {{ createdLabel }}</div>
        </div>
        <div class="column is-narrow p-0 is-flex is-align-items-center">
          <b-tag class="import-job-list-card__status" type="is-light" size="is-small" rounded>
            {{ statusLabelDisplay }}
          </b-tag>
        </div>
      </div>
      <div class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless">
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Type</span>
          <div class="import-job-list-card__val">{{ typeLabel }}</div>
        </div>
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Progress</span>
          <div class="import-job-list-card__val">{{ progressLabel }}</div>
        </div>
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Rows</span>
          <div class="import-job-list-card__val">{{ totalRowsLabel }}</div>
        </div>
      </div>
      <div v-if="job.error" class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless">
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Error</span>
          <div class="import-job-list-card__val has-text-danger">{{ job.error }}</div>
        </div>
      </div>
      <div
        v-if="rowErrorsList.length"
        class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless"
      >
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Row errors</span>
          <div class="import-job-list-card__scroll">
            <ul class="import-job-list-card__errlist">
              <li v-for="(re, idx) in rowErrorsList" :key="idx" class="import-job-list-card__erritem has-text-danger">
                Row {{ re.index + 1 }}: {{ re.message }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        v-if="showCatalogItemsSection"
        class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless"
      >
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Catalog items created</span>
          <div class="import-job-list-card__scroll">
            <ul v-if="importedCatalogItemsList.length" class="import-job-list-card__errlist">
              <li v-for="(it, idx) in importedCatalogItemsList" :key="'c' + idx" class="import-job-list-card__erritem">
                Row {{ it.index + 1 }}: {{ catalogItemNameToIdLine(it) }}
              </li>
            </ul>
            <p v-else class="import-job-list-card__fallback">
              {{ catalogItemsCreatedFallbackText }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="createdSuppliersList.length"
        class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless"
      >
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">Suppliers created (MLHIM)</span>
          <div class="import-job-list-card__scroll">
            <ul class="import-job-list-card__errlist">
              <li v-for="(c, idx) in createdSuppliersList" :key="'s' + idx" class="import-job-list-card__erritem">
                Row {{ c.index + 1 }}: {{ supplierNameToIdLine(c) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        v-if="createdDetailListValuesList.length"
        class="import-job-list-card__row columns is-mobile is-variable is-1 is-marginless"
      >
        <div class="column p-0">
          <span class="import-job-list-card__label is-uppercase has-text-grey-light">
            New field values (not in type lists)
          </span>
          <div class="import-job-list-card__meta import-job-list-card__hint">Angles, diameters, brands, etc.</div>
          <div class="import-job-list-card__scroll">
            <ul class="import-job-list-card__errlist">
              <li
                v-for="(d, idx) in createdDetailListValuesList"
                :key="'d' + idx"
                class="import-job-list-card__erritem"
              >
                Row {{ d.index + 1 }}: {{ fieldLabel(d.field) }} — {{ d.value }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CatalogItemImport } from '@newmoon-org/shared';

import { formatDate } from '@/service/dateFormatting.service';

const { TYPES, TYPE_TO_EXTRA } = CatalogItemImport;

export default {
  name: 'CatalogItemImportJobCard',
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  computed: {
    statusLabelDisplay() {
      const s = this.job.status;
      if (s == null || s === '') return '—';
      return String(s).toUpperCase();
    },
    cardClass() {
      const s = String(this.job.status || '').toLowerCase();
      if (s === 'queued') return 'import-job-list-card--queued';
      if (s === 'processing') return 'import-job-list-card--processing';
      if (s === 'completed') return 'import-job-list-card--completed';
      if (s === 'failed') return 'import-job-list-card--failed';
      return 'import-job-list-card--unknown';
    },
    typeLabel() {
      const v = this.job.catalogItemType;
      if (v == null) return '—';
      const o = TYPES.find(t => t.value === String(v).toLowerCase());
      return o ? o.label : String(v);
    },
    createdLabel() {
      const d = this.toDate(this.job.createdAt);
      return d ? formatDate(d) : '—';
    },
    importerLabel() {
      const x = this.job.createdBy;
      if (!x) return '';
      const a = String(x.firstName || '').trim();
      const b = String(x.lastName || '').trim();
      const n = [a, b].filter(Boolean).join(' ');
      if (n) return n;
      return String(x.code || '').trim();
    },
    progressLabel() {
      const p = this.job.progress;
      if (!p || p.total == null) return '—';
      const d = p.done != null ? p.done : 0;
      return `${d} / ${p.total} created`;
    },
    totalRowsLabel() {
      const p = this.job.progress;
      return p && p.total != null ? String(p.total) : '—';
    },
    progressDoneCount() {
      const p = this.job.progress;
      if (!p || p.done == null) return 0;
      const n = Number(p.done);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    rowErrorsList() {
      const list = this.job.finishedErrors;
      if (!Array.isArray(list) || !list.length) return [];
      return list;
    },
    createdSuppliersList() {
      const list = this.job.createdSuppliers;
      if (!Array.isArray(list) || !list.length) return [];
      return list;
    },
    createdDetailListValuesList() {
      const list = this.job.createdDetailListValues;
      if (!Array.isArray(list) || !list.length) return [];
      return list;
    },
    importedCatalogItemsList() {
      const raw = this.job.importedCatalogItems ?? this.job.imported_catalog_items;
      if (!Array.isArray(raw) || !raw.length) return [];
      return raw
        .map((x, i) => {
          const catalogItemId = String(x?.catalogItemId ?? x?.catalog_item_id ?? '').trim();
          const name = x?.name != null ? String(x.name).trim() : '';
          const index = typeof x?.index === 'number' && !Number.isNaN(x.index) ? x.index : i;
          return { index, catalogItemId, name };
        })
        .filter(x => x.catalogItemId || x.name);
    },
    showCatalogItemsSection() {
      if (this.importedCatalogItemsList.length) return true;
      const s = String(this.job.status || '').toLowerCase();
      if (this.progressDoneCount <= 0) return false;
      return s === 'completed' || s === 'failed';
    },
    catalogItemsCreatedFallbackText() {
      const n = this.progressDoneCount;
      return `${n} item(s) saved without id/name rows on this job.`;
    },
  },
  methods: {
    fieldLabel(fieldKey) {
      const t = String(this.job.catalogItemType || '').toLowerCase();
      const extra = TYPE_TO_EXTRA[t] || {};
      const hit = Object.values(extra).find(e => e.key === fieldKey);
      return hit ? hit.label : fieldKey;
    },
    supplierNameToIdLine(c) {
      const name = c.name != null && String(c.name).trim() ? String(c.name).trim() : '';
      const id = c.id != null ? String(c.id).trim() : '';
      if (name && id) return `${name} → ${id}`;
      return id || name || '—';
    },
    catalogItemNameToIdLine(it) {
      const name = it.name != null && String(it.name).trim() ? String(it.name).trim() : '';
      const id = it.catalogItemId != null ? String(it.catalogItemId).trim() : '';
      if (name && id) return `${name} → ${id}`;
      return id || name || '—';
    },
    toDate(ct) {
      if (ct == null) return null;
      if (typeof ct.toDate === 'function') return ct.toDate();
      if (ct.seconds != null) return new Date(ct.seconds * 1000);
      return null;
    },
  },
};
</script>

<style scoped>
.import-job-list-card {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(10, 10, 10, 0.05);
  border-left-width: 4px;
  border-left-style: solid;
}
.import-job-list-card--queued {
  --import-job-bar: #3e8ed0;
}
.import-job-list-card--processing {
  --import-job-bar: #946c00;
}
.import-job-list-card--completed {
  --import-job-bar: #48c78e;
}
.import-job-list-card--failed {
  --import-job-bar: #f14668;
}
.import-job-list-card--unknown {
  --import-job-bar: #b5b5b5;
}
.import-job-list-card--queued,
.import-job-list-card--processing,
.import-job-list-card--completed,
.import-job-list-card--failed,
.import-job-list-card--unknown {
  border-left-color: var(--import-job-bar);
}
.import-job-list-card--queued :deep(.tag),
.import-job-list-card--processing :deep(.tag),
.import-job-list-card--completed :deep(.tag),
.import-job-list-card--failed :deep(.tag) {
  background-color: var(--import-job-bar) !important;
  color: #fff !important;
  border-color: transparent !important;
}
.import-job-list-card--unknown :deep(.tag) {
  background-color: var(--import-job-bar) !important;
  color: #363636 !important;
  border-color: transparent !important;
}
.import-job-list-card__content {
  padding: 0.5rem 0.65rem 0.45rem 0.75rem;
}
.import-job-list-card__row + .import-job-list-card__row {
  margin-top: 0.3rem;
}
.import-job-list-card__title {
  font-size: 0.8rem;
  line-height: 1.2;
  margin: 0 0 0.1rem 0;
}
.import-job-list-card__meta {
  font-size: 0.7rem;
  line-height: 1.2;
  margin: 0;
}
.import-job-list-card__label {
  display: block;
  font-size: 0.6rem;
  line-height: 1.1;
  letter-spacing: 0.03em;
  margin-bottom: 0.1rem;
}
.import-job-list-card__val {
  font-size: 0.7rem;
  line-height: 1.2;
  margin: 0;
}
.import-job-list-card__status {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.08em;
}
.import-job-list-card__errlist {
  margin: 0.15rem 0 0 1rem;
  padding: 0;
  font-size: 0.7rem;
  line-height: 1.25;
}
.import-job-list-card__erritem {
  margin: 0.1rem 0;
}
.import-job-list-card__hint {
  font-size: 0.65rem;
  margin: 0 0 0.15rem 0;
  line-height: 1.2;
}
.import-job-list-card__scroll {
  max-height: calc(5 * (0.7rem * 1.25 + 0.2rem));
  overflow-y: auto;
  margin-top: 0.15rem;
}
.import-job-list-card__fallback {
  margin: 0.15rem 0 0 0;
  padding: 0;
  font-size: 0.7rem;
  line-height: 1.25;
  color: #7a7a7a;
}
@media screen and (max-width: 768px) {
  .import-job-list-card__content {
    padding: 0.45rem 0.5rem;
  }
  .import-job-list-card__title {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .import-job-list-card__row .column.is-narrow {
    margin-top: 0.25rem;
  }
}
</style>
